import { Component, NgZone, OnInit } from '@angular/core';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable, of } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import {
  AIASSIST_FIELDS_ENTITY_NAME,
  AIASSIST_FIELDS_ENTITY_NAME_LABEL_PAIR,
  CONDITIONAL_FIELDS_ENTITY_NAME,
  IFieldMeta,
} from '../../../interfaces/imetadata';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../../../services/content-control-to-field-map.service';
import { ControllerService } from '../../../services/controller.service';
import { ActiveContentControlMonitor } from '../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../services/document-monitors/content-control-id';
import { LoggingService } from '../../../services/logging.service';
import { systemDefaults } from '../../../utils/fieldconfighelper';
import { FieldContext } from '../../../utils/fieldcontext';
import { FilterableItem, IFilterableItem } from '../../filterable-list/filterable-item';
import { IValidStateChangeProvider, IVisibleStateProvider, ValidStateProvider } from '../valid-state-provider';
import { AiAssistPromptFactory } from './ai-assist-prompt.factory';
import { IPrompt } from './iprompt';

@Component({
  selector: 'app-ai-assist-prompt',
  templateUrl: './ai-assist-prompt.component.html',
  styleUrls: ['../field-selection.component.scss', '../../../../assets/css-imports/adv2-word-addin/generic-tab-with-form.scss'],
  standalone: false,
})
export class AiAssistPromptComponent implements OnInit, IValidStateChangeProvider, IVisibleStateProvider {
  private valid = new ValidStateProvider();
  isVisible: Observable<boolean> = of(false);

  promptList: IFilterableItem[] = [];

  constructor(
    private aiAssistPromptFactory: AiAssistPromptFactory,
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private controllerService: ControllerService,
    private identityStorage: IdentityStorage,
    private messageNotificationService: MessageNotificationService,
    private ngZone: NgZone,
    private loggingService: LoggingService
  ) {
    activeContentControlMonitor.activeContentControlChanged$.subscribe((acc) => this.activeControlChanged(acc));
  }

  ngOnInit(): void {
    const namespace = this.identityStorage.get().namespace;
    const aiAssistPromptService = this.aiAssistPromptFactory.getInstance(namespace);
    if (!aiAssistPromptService) {
      return;
    }
    aiAssistPromptService.getPrompts().subscribe((prompts: IPrompt[]) => {
      this.promptList = prompts.map((promptData: IPrompt) => {
        return FilterableItem.newFilterableItem(
          promptData.id,
          promptData.displayName,
          promptData,
          aiAssistPromptService.getIcon(promptData)
        );
      });
    });

    this.isVisible = aiAssistPromptService.isEnabled();
  }

  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }

  get visible(): Observable<boolean> {
    return this.isVisible;
  }

  onFieldInserted(event: IFilterableItem) {
    Word.run(async (context) => {
      try {
        const namespace = this.identityStorage.get().namespace;

        // hack to make sure namespace settings exist by the time we get to the FunctionsModelService call
        // cannot put it FunctionsModelService class due to circular dependency
        // would have been handled correctly if the controllerService.insert did all the work below
        // re-above last line, doesn't it?
        await this.controllerService.getOrCreateNamespaceSettings(namespace);

        const fieldContext: FieldContext = this.buildFieldContext(namespace, event.data);

        await this.controllerService.insert(context, fieldContext);
        await context.sync();
      } catch (err: any) {
        this.loggingService.error('AiAssistPromptComponent', 'onFieldInserted', {
          message: 'Failed to insert AI assist prompt field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to insert AI assist prompt field.', err);
      }
    });
  }

  private buildFieldContext(namespace: string, promptData: IPrompt): FieldContext {
    const field: IFieldMeta = {
      namespace: namespace,
      path: `aiassist`, // leaf is used for the xpath element name
      label: promptData.displayName,
      type: 'ai-assist-prompt',
    };

    const fieldContext: FieldContext = new FieldContext(namespace, AIASSIST_FIELDS_ENTITY_NAME_LABEL_PAIR, field);
    fieldContext.stringFormat = {
      ...systemDefaults.formatting.stringFormat,
      renderHtml: true,
    };
    fieldContext.aiAssistFieldConfig = {
      corpId: promptData.corpId,
      promptId: promptData.id,
      type: fieldContext.field.type,
      functionId: undefined,
    };

    return fieldContext;
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cscript-field - active content control changed handler', 'color:#0000FF');
    if (this.isDocusignField(activeContentControl)) {
      this.valid.setValid(false);
      return;
    }
    this.ngZone.run(() => {
      const activeFieldContext = activeContentControl ? this.contentControlToFieldMapService.get(activeContentControl.id) : undefined;
      const isAiAssistField = this.isAiAssistField(activeFieldContext);
      const isConditionalField = this.isConditionalField(activeFieldContext);
      this.valid.setValid(!activeFieldContext || isAiAssistField || isConditionalField);
    });
  }

  private isAiAssistField(activeFieldContext: SerializedFieldContext | undefined): boolean {
    return activeFieldContext?.entity === AIASSIST_FIELDS_ENTITY_NAME;
  }

  private isConditionalField(activeFieldContext: SerializedFieldContext | undefined): boolean {
    return activeFieldContext?.entity === CONDITIONAL_FIELDS_ENTITY_NAME;
  }

  private isDocusignField(activeContentControl?: ContentControlId): boolean {
    return activeContentControl ? activeContentControl.tag.startsWith('docusign-') : false;
  }
}
